import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useMemo } from 'react';
import { VCData, TeamMember } from '../../types/vc';
import { SectionTitle } from './SectionTitle';
import { TeamList } from './TeamList';
import { ExitList } from './ExitList';
import { NetworkGraph } from '../NetworkGraph/NetworkGraph';
import { transformVCToGraph } from '../../utils/graphTransform';

interface VCModalProps {
    vc: VCData | null;
    isOpen: boolean;
    onClose: () => void;
}

const DESCRIPTION_LENGTH = 300; // Characters to show initially

function formatMoney(amount: number) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(amount);
}

function findFounderImage(founder: { value: string }, employees: TeamMember[] = []): string | undefined {
    const employee = employees.find(emp => emp.name === founder.value);
    return employee?.image;
}

function PortfolioList({ companies }: { companies: VCData[] }) {
    if (!companies || companies.length === 0) return null;

    return (
        <div>
            <SectionTitle>Portfolio Companies</SectionTitle>
            <div className="mt-4 grid grid-cols-1 gap-4">
                {companies.slice(0, 5).map((company, index) => {
                    const logoUrl = company.logo || company.data?.imageUrl;

                    return (
                        <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                            <div className="flex-shrink-0">
                                {logoUrl && (
                                    <img
                                        src={logoUrl}
                                        alt={`${company.name} logo`}
                                        className="h-12 w-12 rounded-lg object-contain bg-white border border-gray-200"
                                    />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                        {company.name}
                                    </h4>
                                </div>
                                {company.bdRaw?.founders && company.bdRaw.founders.length > 0 && (
                                    <div className="mt-2">
                                        <div className="flex items-center space-x-2">
                                            <div className="flex -space-x-2">
                                                {company.bdRaw.founders.map((founder) => {
                                                    const founderImage = findFounderImage(founder, company.bdRaw?.current_employees);
                                                    return (
                                                        <div key={founder.id} className="relative">
                                                            <img
                                                                src={founderImage || 'https://www.gravatar.com/avatar/?d=mp'}
                                                                alt={founder.value}
                                                                className="w-6 h-6 rounded-full border-2 border-white object-cover"
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {company.bdRaw.founders.map(founder => founder.value).join(', ')}
                                            </p>
                                        </div>
                                        {company.bdRaw?.full_description && (
                                            <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                                                {company.bdRaw.full_description}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function VCModal({ vc, isOpen, onClose }: VCModalProps) {
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const graphData = useMemo(() => {
        if (!vc) return null;
        return transformVCToGraph(vc);
    }, [vc]);

    if (!vc || !vc.data || !vc.bdRaw) return null;

    const {
        name,
        data: { imageUrl, website, linkedin, twitter },
        bdRaw: {
            investor_type = 'Venture Capital',
            investment_stage = '',
            num_investments = 0,
            num_exits = 0,
            current_employees = [],
            featured_list = [],
            exits = [],
            contact_email,
            full_description
        },
        portfolio = []
    } = vc;

    const description = full_description || vc.data.description;

    if (!description) return null;

    const isLongDescription = description.length > DESCRIPTION_LENGTH;
    const displayedDescription = isDescriptionExpanded ? description : description.slice(0, DESCRIPTION_LENGTH);

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 md:pl-10">
                            <Transition.Child
                                as={Fragment}
                                enter="transform transition ease-in-out duration-500"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-500"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel className="pointer-events-auto w-screen max-w-[90rem]">
                                    <div className="flex h-full flex-col md:flex-row">
                                        {/* Network Graph Section - Hidden on Mobile */}
                                        <div className="hidden md:block flex-1 bg-white border-r border-gray-200">
                                            <div className="h-full">
                                                {/* Graph Legend */}
                                                <div className="absolute top-4 right-[calc(600px+1rem)] bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm z-10 border border-gray-100">
                                                    <div className="flex flex-col space-y-2">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="w-2.5 h-2.5 rounded-full bg-[#60A5FA]" />
                                                            <span className="text-xs text-gray-600 font-medium">VC</span>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <div className="w-2.5 h-2.5 rounded-full bg-[#34D399]" />
                                                            <span className="text-xs text-gray-600 font-medium">Portfolio</span>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <div className="w-2.5 h-2.5 rounded-full bg-[#C084FC]" />
                                                            <span className="text-xs text-gray-600 font-medium">Team</span>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <div className="w-2.5 h-2.5 rounded-full bg-[#F472B6]" />
                                                            <span className="text-xs text-gray-600 font-medium">Founders</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {graphData && (
                                                    <NetworkGraph
                                                        data={graphData}
                                                        width={900}
                                                        height={900}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* VC Info Section - Full Width on Mobile */}
                                        <div className="relative w-full md:w-[600px] flex flex-col bg-white h-screen md:h-full">
                                            {/* Close button - Fixed on mobile */}
                                            <button
                                                type="button"
                                                className="fixed md:absolute top-3 right-4 md:right-3 z-20 rounded-md bg-white/80 backdrop-blur-sm p-2 text-gray-400 hover:text-gray-500 focus:outline-none"
                                                onClick={onClose}
                                            >
                                                <span className="sr-only">Close</span>
                                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>

                                            {/* Sticky Header */}
                                            <div className="sticky top-0 z-10 bg-white px-4 md:px-6 py-4 border-b border-gray-200">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center space-x-3 md:space-x-4">
                                                        <div className="h-12 w-12 md:h-16 md:w-16 flex-shrink-0">
                                                            {imageUrl && (
                                                                <img
                                                                    src={imageUrl}
                                                                    alt={`${name} logo`}
                                                                    className="h-12 w-12 md:h-16 md:w-16 rounded-lg object-contain bg-white"
                                                                />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <Dialog.Title className="text-lg md:text-xl font-semibold text-gray-900">
                                                                {name}
                                                            </Dialog.Title>
                                                            <p className="text-xs md:text-sm text-gray-500">{investor_type}</p>
                                                            <div className="mt-1 flex items-center space-x-2 flex-wrap">
                                                                {investment_stage && (
                                                                    <>
                                                                        <span className="text-xs text-gray-500">
                                                                            {investment_stage}
                                                                        </span>
                                                                        <span className="text-gray-300">•</span>
                                                                    </>
                                                                )}
                                                                <span className="text-xs text-gray-500">
                                                                    {num_investments} Investments
                                                                </span>
                                                                <span className="text-gray-300">•</span>
                                                                <span className="text-xs text-gray-500">
                                                                    {num_exits} Exits
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Scrollable Content */}
                                            <div className="flex-1 overflow-y-auto h-[calc(100vh-80px)] md:h-auto">
                                                <div className="px-4 md:px-6 py-4 md:py-6 space-y-6 md:space-y-8">
                                                    {/* Description */}
                                                    <div>
                                                        <SectionTitle>About</SectionTitle>
                                                        <div className="space-y-2">
                                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                                {displayedDescription}
                                                                {!isDescriptionExpanded && isLongDescription && '...'}
                                                            </p>
                                                            {isLongDescription && (
                                                                <button
                                                                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                                                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium focus:outline-none focus:underline"
                                                                >
                                                                    {isDescriptionExpanded ? 'Show Less' : 'Show More'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Team */}
                                                    {current_employees.length > 0 && (
                                                        <TeamList members={current_employees} />
                                                    )}

                                                    {/* Featured Lists */}
                                                    {featured_list.length > 0 && (
                                                        <div>
                                                            <SectionTitle>Investment Focus</SectionTitle>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                {featured_list.map((list, index) => (
                                                                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                                                                        <h5 className="text-sm font-medium text-gray-900">{list.title}</h5>
                                                                        <div className="mt-2 space-y-1">
                                                                            <p className="text-xs text-gray-600">
                                                                                {list.org_num} Organizations • {list.org_num_investors} Investors
                                                                            </p>
                                                                            <p className="text-xs text-gray-600">
                                                                                Total Funding: {formatMoney(list.org_funding_total.value_usd)}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Portfolio Companies */}
                                                    {portfolio && portfolio.length > 0 && (
                                                        <PortfolioList companies={portfolio} />
                                                    )}

                                                    {/* Exits */}
                                                    {exits.length > 0 && (
                                                        <ExitList exits={exits} />
                                                    )}

                                                    {/* Contact Information */}
                                                    {contact_email && (
                                                        <div>
                                                            <SectionTitle>Contact</SectionTitle>
                                                            <a
                                                                href={`mailto:${contact_email}`}
                                                                className="text-sm text-indigo-600 hover:text-indigo-800"
                                                            >
                                                                {contact_email}
                                                            </a>
                                                        </div>
                                                    )}

                                                    {/* Links - Adjusted for mobile */}
                                                    {(website || linkedin || twitter) && (
                                                        <div className="border-t pt-4 md:pt-6">
                                                            <div className="flex flex-wrap gap-2">
                                                                {website && (
                                                                    <a
                                                                        href={website}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="inline-flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                                                                    >
                                                                        <svg className="h-5 w-5 md:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                                                        </svg>
                                                                        <span className="hidden md:inline">Visit Website</span>
                                                                    </a>
                                                                )}
                                                                {linkedin && (
                                                                    <a
                                                                        href={linkedin}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="inline-flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                                                    >
                                                                        <svg className="h-5 w-5 text-blue-600 md:mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                                                        </svg>
                                                                        <span className="hidden md:inline">LinkedIn</span>
                                                                    </a>
                                                                )}
                                                                {twitter && (
                                                                    <a
                                                                        href={twitter}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="inline-flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                                                    >
                                                                        <svg className="h-5 w-5 text-blue-400 md:mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                                                        </svg>
                                                                        <span className="hidden md:inline">Twitter</span>
                                                                    </a>
                                                                )}
                                                                {vc.permalink && (
                                                                    <a
                                                                        href={vc.permalink}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="inline-flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                                                    >
                                                                        <svg className="h-5 w-5 text-gray-500 md:mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                                            <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-1 17l-5-5 1.41-1.41 3.59 3.59 7.59-7.59 1.41 1.41-9 9z" />
                                                                        </svg>
                                                                        <span className="hidden md:inline">View on Crunchbase</span>
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
} 